import type { ReactNode } from "react";
import { STEPS, type Step } from "@hearing/shared";
import { useScreener } from "../state.js";

const STEP_LABELS: Record<Step, string> = {
  welcome: "Welcome",
  headphones: "Headphones",
  "set-volume": "Volume",
  test: "Hearing test",
  questions: "About you",
  details: "Your details",
  results: "Results",
};

export function Layout({
  children,
  step,
}: {
  children: ReactNode;
  step?: Step;
}) {
  const { config } = useScreener();
  const brand = config?.brand ?? {
    name: "Hearing Expert",
    teamName: "Hearing Expert audiology team",
    logoUrl: "/brands/hearing-expert.png",
  };

  return (
    <div className="app">
      <header className="app-header">
        <div className="brand">
          <img className="brand-logo" src={brand.logoUrl} alt={brand.name} />
          <span className="brand-name">
            <small>Online Hearing Check</small>
          </span>
        </div>
      </header>
      {step ? <ProgressBar current={step} /> : null}
      <main className="card">{children}</main>
      <footer className="app-footer muted">
        This is a hearing screening tool, not a medical diagnosis. Your data is
        handled by the {brand.teamName} in line with our privacy policy.
      </footer>
    </div>
  );
}

function ProgressBar({ current }: { current: Step }) {
  const currentIndex = STEPS.indexOf(current);
  const pct = (currentIndex / (STEPS.length - 1)) * 100;
  return (
    <div
      className="flow-progress"
      role="progressbar"
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={Math.round(pct)}
      aria-label={`Step ${currentIndex + 1} of ${STEPS.length}: ${STEP_LABELS[current]}`}
    >
      <div className="flow-progress-meta">
        <div className="flow-progress-label">{STEP_LABELS[current]}</div>
        <div className="flow-progress-count">
          Step {currentIndex + 1} of {STEPS.length}
        </div>
      </div>
      <div className="flow-progress-track">
        <span style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}
